/**
 * BranchLog (RFC 0215: Log substrate).
 *
 * The client surface: wraps a host-owned Automerge handle and exposes ergonomic
 * read / write / subscribe / project / coordination over the pure core. Workers
 * (0216/0217) use append / mutate / subscribe; UI uses subscribe + project*.
 *
 * The Automerge document holds the canonical Searugast on-doc shape; this class
 * crosses the serialization boundary (codec.ts) so callers work with the
 * idiomatic in-memory model. Id and timestamp generation live here so the pure
 * core stays deterministic.
 */

import { Automerge } from '@automerge/automerge-repo/slim'
import { v4 as uuidv4, v5 as uuidv5 } from 'uuid'
import type { ContentStore, LogHandle } from './adapters'
import {
  decodeEntry,
  decodeLogDoc,
  encodeToolUse,
  initThread,
  reencodeScalars,
  THREAD_KEY,
  writeEntry,
  writeToolWorker,
} from './codec'
import { activeToolWorker, inferenceForkWinner } from './coordination'
import { ancestors, continuationHeads, get, heads, lineageHeads } from './dag'
import {
  type Interaction,
  projectInteractions,
} from './projection-interactions'
import {
  type ObjectRef,
  projectObjects,
  projectObjectsWithTombstones,
} from './projection-objects'
import { sessionParticipants } from './projection-participants'
import {
  projectSessionChanges,
  projectSessionLifecycle,
  type SessionChangeRef,
  type SessionLifecycle,
} from './projection-session-changes'
import {
  projectTranscript,
  projectTranscriptForRun,
  projectTranscriptFromHeads,
  type TranscriptMessage,
} from './projection-transcript'
import { peerMaySyncSession, type SessionAccessOptions } from './session-access'
import type { SessionStripActivity } from './strip-activity'
import type {
  DocId,
  Entry,
  EntryId,
  InteractionKind,
  InteractionOutcome,
  LogDoc,
  NewEntry,
  ParticipantTargetKind,
  TimestampMs,
  ToolUseBlock,
  ToolWorker,
  ToolWorkerId,
  TouchKind,
} from './types'
import {
  BRANCH_EXTENSION,
  COMMENT_CLAIMED_EXTENSION,
  COMMENT_REPLIED_EXTENSION,
  EDITED_EXTENSION,
  FIRED_EXTENSION,
  type FiringSignal,
  INTERACTION_ANSWERED_EXTENSION,
  INTERACTION_REQUESTED_EXTENSION,
  MENTIONED_EXTENSION,
  PAGE_HANDED_BACK_EXTENSION,
  PAGE_TAKEN_OVER_EXTENSION,
  PARTICIPANT_EXTENSION,
  PASSED_EXTENSION,
  PUT_DOWN_EXTENSION,
  SCHEDULE_DUE_EXTENSION,
  type ScheduleDueData,
  SESSION_BRANCHED_EXTENSION,
  SESSION_CHANGE_EXTENSION,
  SESSION_DISCARDED_EXTENSION,
  SESSION_MERGED_EXTENSION,
  SESSION_REPLIED_EXTENSION,
  SESSION_STOPPED_EXTENSION,
  STOP_DECIDED_EXTENSION,
  type StopVerdict,
  TOUCHED_EXTENSION,
  UNDONE_EXTENSION,
} from './types'

/** A run is over when it reaches one of these (the set `dispatch-notify` uses
 *  for the same question). */
const RUN_ENDED = new Set(['completed', 'failed', 'cancelled'])

export type EntryPredicate = (e: Entry) => boolean
export type Unsubscribe = () => void

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

/** Only session documents have a small header beside their inline thread. */
function isSessionDocument(d: Draft): boolean {
  return d.id != null && d.title != null
}

/**
 * Keep the header scalar and bounded. This is intentionally at the write
 * boundary: reading the compact composer must never decode the whole log.
 */
function writeStripActivity(
  d: Draft,
  activity: SessionStripActivity | null,
): void {
  if (!isSessionDocument(d)) return
  if (!activity) {
    delete d.stripActivity
    delete d.stripReplyMessageId
    return
  }
  delete d.stripReplyMessageId
  if (activity.kind === 'reply' || activity.kind === 'text') {
    d.stripActivity = {
      kind: new Automerge.ImmutableString(activity.kind),
      text: new Automerge.ImmutableString(activity.text),
      ...(activity.truncated ? { truncated: true } : {}),
    }
  } else if (activity.kind === 'tool') {
    d.stripActivity = {
      kind: new Automerge.ImmutableString('tool'),
      name: new Automerge.ImmutableString(activity.name),
      ...(activity.summary
        ? { summary: new Automerge.ImmutableString(activity.summary) }
        : {}),
    }
  } else {
    d.stripActivity = {
      kind: new Automerge.ImmutableString('question'),
      interactionId: new Automerge.ImmutableString(activity.interactionId),
      bodyJson: new Automerge.ImmutableString(activity.bodyJson),
    }
  }
}

/** Point at the live assistant entry; its scalar text changes in place. */
function writeStripReplySource(d: Draft, entryId: EntryId): void {
  if (!isSessionDocument(d)) return
  delete d.stripActivity
  d.stripReplyMessageId = new Automerge.ImmutableString(entryId)
}

export class BranchLog {
  constructor(
    private readonly handle: LogHandle,
    private readonly content?: ContentStore,
  ) {}

  /**
   * The decoded document, and the entry list off it, for ONE document version.
   *
   * Keyed on the raw Automerge document's own identity. An Automerge document
   * is immutable: reading it twice without a change returns the same object,
   * and any change returns a new one. So `raw === this.decodedFrom` is an exact
   * test for "nothing has changed since we decoded", not a heuristic.
   */
  private decodedFrom?: Record<string, unknown>
  private decoded?: LogDoc
  private entryList?: Entry[]
  /** The interactions projection, one per document version; see `doc()`. */
  private interactions?: Interaction[]

  /**
   * Decode the on-doc Automerge document into the idiomatic in-memory model.
   *
   * MEMOISED, BECAUSE EVERY READ USED TO PAY FOR THE WHOLE LOG. This class
   * calls `doc()` from 22 places and `entries()` from 25 across mod-core, and
   * each call re-materialised the Automerge document and re-decoded it. On a
   * session log of a few thousand entries, asking a one-line question like
   * `branchFor()` or `currentTurn()` cost a full materialisation, and the
   * answers are read in loops.
   *
   * A profile of the daemon put `entries` at 52% of all non-idle time, its self
   * time being the `Object.values` below; a native sample of a daemon spinning
   * at 100% of a core agreed, with `Object::GetOwnPropertyNames` on top.
   */
  private doc(): LogDoc {
    const raw = this.handle.doc() as Record<string, unknown>
    if (this.decoded && this.decodedFrom === raw) return this.decoded
    this.decoded = decodeLogDoc(raw)
    this.decodedFrom = raw
    this.entryList = undefined
    this.interactions = undefined
    return this.decoded
  }

  // ---- read ----

  /** Protected sessions require an agent-key claim gate, even when a legacy
   * run still names a harness rather than a key. Read fresh on every drain. */
  isProtectedSession(): boolean {
    const doc = this.handle.doc() as { sharingGroupId?: unknown }
    return doc.sharingGroupId != null
  }

  /** The actual document identity, not its editable `id` header. */
  documentId(): string {
    return String(this.handle.documentId)
  }

  /**
   * The entries, as one array per document version.
   *
   * SHARED, NOT COPIED. No caller in the tree mutates this array: every use is
   * an iteration or a read. Sorting or splicing the result would now be seen by
   * the next caller, so a caller that needs to reorder takes its own copy.
   */
  entries(): Entry[] {
    const doc = this.doc()
    if (!this.entryList) this.entryList = Object.values(doc.entries)
    return this.entryList
  }

  get(id: EntryId): Entry | undefined {
    return get(this.doc(), id)
  }

  heads(): EntryId[] {
    return heads(this.doc())
  }

  ancestors(id: EntryId): Entry[] {
    return ancestors(this.doc(), id)
  }

  // ---- write ----

  append(payload: NewEntry, parents: EntryId[]): EntryId {
    return this.writeEntryWithId(uuidv4(), payload, parents)
  }

  /** Stable ids let two replicas converge on one occurrence entry. */
  appendWithId(id: EntryId, payload: NewEntry, parents: EntryId[]): EntryId {
    if (this.get(id)) return id
    return this.writeEntryWithId(id, payload, parents)
  }

  private writeEntryWithId(
    id: EntryId,
    payload: NewEntry,
    parents: EntryId[],
  ): EntryId {
    const createdAt = Date.now()
    this.handle.change((d: Draft) => {
      if (!d[THREAD_KEY]) initThread(d)
      writeEntry(d[THREAD_KEY], id, parents, createdAt, payload)
      if (payload.type === 'message') {
        const message = payload.payload
        if (message.role === 'user') writeStripActivity(d, null)
        else if (message.role === 'assistant') writeStripReplySource(d, id)
      }
    })
    return id
  }

  /**
   * Append a linear chain of entries in a SINGLE Automerge change. Each entry's
   * parent is the previous appended id (seeded by `parents`). Importing a large
   * captured transcript via per-message `append()` does one change per message
   * (hundreds of changes), which blocks the event loop and freezes the UI on
   * open; batching them into one change makes import ~O(1) transactions.
   * Returns the appended ids in order.
   */
  appendMany(payloads: NewEntry[], parents: EntryId[]): EntryId[] {
    if (payloads.length === 0) return []
    const ids = payloads.map(() => uuidv4())
    const createdAt = Date.now()
    this.handle.change((d: Draft) => {
      if (!d[THREAD_KEY]) initThread(d)
      let chain = parents
      for (let i = 0; i < payloads.length; i++) {
        writeEntry(d[THREAD_KEY], ids[i], chain, createdAt, payloads[i])
        chain = [ids[i]]
      }
    })
    return ids
  }

  /**
   * Append a developer- or first-party-defined extension event (RFC 0215 §3.8).
   * `extensionType` is namespaced (owner/package-prefixed); `payload` is opaque
   * and round-trips for peers without a handler.
   */
  appendExtension(
    extensionType: string,
    version: number,
    payload: Record<string, unknown>,
    parents: EntryId[],
  ): EntryId {
    return this.append(
      { type: 'extension', payload: { extensionType, version, payload } },
      parents,
    )
  }

  /**
   * RFC 0233 — the single emit path for a participant entry, which is how a
   * member is ADMITTED to a session. `SessionRepository.appendParticipantEntry`
   * routes through here, so every client records it byte-identically (RFC 0233
   * risk #2). Stores `memberId` (the auth-tier identity), never a handle, with a
   * flat on-doc target (`targetKind`/`targetId`). Returns the new entry id.
   *
   * This is the only way access to a session widens, which is why RFC 0292 took
   * the word `mention` off it: a mention is a reference inside prose and grants
   * nothing, and the two must not share a name for a moment.
   */
  appendParticipant(payload: {
    memberId: string
    by: string
    target: { kind: ParticipantTargetKind; id: string }
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      PARTICIPANT_EXTENSION,
      1,
      {
        memberId: payload.memberId,
        by: payload.by,
        targetKind: payload.target.kind,
        targetId: payload.target.id,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /**
   * RFC 0235 — the single emit path for a session's code change (dual tracking).
   * Links the session to its VCS change (ref + touched paths); the log carries
   * the linkage, git/jj carries the bytes. Stored flat on-doc to match the other
   * extension payloads; `projectSessionChanges` re-nests `vcsRef`. Returns the
   * new entry id.
   */
  appendSessionChange(payload: {
    sessionId: string
    vcsRef: { backend: string; commit: string; ref: string }
    files: string[]
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      SESSION_CHANGE_EXTENSION,
      1,
      {
        sessionId: payload.sessionId,
        vcsBackend: payload.vcsRef.backend,
        vcsCommit: payload.vcsRef.commit,
        vcsBranch: payload.vcsRef.ref,
        files: [...payload.files],
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /** RFC 0235 — record that a session's change was disregarded (branch dropped). */
  appendSessionDiscarded(payload: {
    sessionId: string
    vcsBranch: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      SESSION_DISCARDED_EXTENSION,
      1,
      {
        sessionId: payload.sessionId,
        vcsBranch: payload.vcsBranch,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /**
   * RFC 0302 S4 — record that this session is a branch of another, and of what.
   *
   * Written on the CHILD's log before its first run, so the very first thing
   * anybody reading this session finds is where it came from.
   */
  appendSessionBranched(payload: {
    sourceSessionId: string
    sourceMessageId?: string
    sourceHeads: readonly string[]
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      SESSION_BRANCHED_EXTENSION,
      1,
      {
        sourceSessionId: payload.sourceSessionId,
        sourceMessageId: payload.sourceMessageId ?? '',
        sourceHeads: [...payload.sourceHeads],
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /** RFC 0235 — record that a session's change was merged into its base. */
  appendSessionMerged(payload: {
    sessionId: string
    vcsBranch: string
    vcsCommit: string
    vcsInto: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      SESSION_MERGED_EXTENSION,
      1,
      {
        sessionId: payload.sessionId,
        vcsBranch: payload.vcsBranch,
        vcsCommit: payload.vcsCommit,
        vcsInto: payload.vcsInto,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /**
   * The single emit path for a turn that stops to ask a human.
   *
   * Appending is all the asking there is: the agent blocks on the correlated
   * `interactionId`, and whichever surface is syncing this session renders the
   * request and answers it. See `InteractionRequestedData` for why the
   * kind-specific body is a JSON string rather than a nested object.
   */
  appendInteractionRequested(payload: {
    interactionId: string
    sessionId: string
    kind: InteractionKind
    title: string
    bodyJson: string
    at?: TimestampMs
  }): EntryId {
    const id = this.appendExtension(
      INTERACTION_REQUESTED_EXTENSION,
      1,
      {
        interactionId: payload.interactionId,
        sessionId: payload.sessionId,
        kind: payload.kind,
        title: payload.title,
        bodyJson: payload.bodyJson,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
    if (payload.kind === 'question' && payload.bodyJson.length <= 32_000) {
      this.handle.change((d: Draft) =>
        writeStripActivity(d, {
          kind: 'question',
          interactionId: payload.interactionId,
          bodyJson: payload.bodyJson,
        }),
      )
    }
    return id
  }

  /**
   * Answer (or cancel) a pending interaction. Written by the surface a human is
   * actually looking at, which is generally NOT the peer that asked.
   */
  appendInteractionAnswered(payload: {
    interactionId: string
    sessionId: string
    outcome: InteractionOutcome
    answerJson?: string
    reason?: string
    by?: string
    at?: TimestampMs
  }): EntryId {
    const id = this.appendExtension(
      INTERACTION_ANSWERED_EXTENSION,
      1,
      {
        interactionId: payload.interactionId,
        sessionId: payload.sessionId,
        outcome: payload.outcome,
        answerJson: payload.answerJson ?? '',
        // Flat scalars only: an absent reason is an empty string on-doc, and
        // the projection maps it back to undefined.
        reason: payload.reason ?? '',
        by: payload.by ?? '',
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
    this.handle.change((d: Draft) => {
      const current = d.stripActivity
      if (
        current &&
        String(current.kind) === 'question' &&
        String(current.interactionId) === payload.interactionId
      )
        writeStripActivity(d, null)
    })
    return id
  }

  /**
   * RFC 0348 3a: the single emit path for "an agent in the World stopped",
   * written on the World agent's log. One line, like a dispatch reply.
   */
  appendSessionStopped(payload: {
    sessionId: string
    runId: string
    /** RFC 0348 3h: which event this is, `ended` or `asked`. */
    event?: 'ended' | 'asked'
    status: 'started' | 'completed' | 'failed' | 'cancelled' | 'asking'
    interactionId?: string
    summary: string
    answeredInARow?: number
    preview?: string
    seen?: boolean
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      SESSION_STOPPED_EXTENSION,
      1,
      {
        sessionId: payload.sessionId,
        runId: payload.runId,
        ...(payload.event ? { event: payload.event } : {}),
        status: payload.status,
        interactionId: payload.interactionId ?? '',
        summary: payload.summary,
        answeredInARow: payload.answeredInARow ?? 0,
        preview: payload.preview ?? '',
        ...(payload.seen ? { seen: true } : {}),
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /**
   * RFC 0350 3d: a reminder came due. Idempotent on `key`: when this log
   * already holds an event for the occurrence, that entry's id is returned
   * and nothing is written, so a retried delivery appends nothing.
   */
  appendScheduleDue(
    payload: Omit<ScheduleDueData, 'at'> & { at?: TimestampMs },
  ): { entryId: EntryId; appended: boolean } {
    const existing = this.scheduleDueEntry(payload.key)
    if (existing) return { entryId: existing, appended: false }
    const entryId = this.writeEntryWithId(
      uuidv5(payload.key, 'f9e7214c-05aa-4f50-a0ca-32670f738bd5'),
      {
        type: 'extension',
        payload: {
          extensionType: SCHEDULE_DUE_EXTENSION,
          version: 1,
          payload: {
            scheduleId: payload.scheduleId,
            key: payload.key,
            dueAt: payload.dueAt,
            late: payload.late,
            text: payload.text,
            wake: payload.wake,
            at: payload.at ?? Date.now(),
          },
        },
      },
      this.heads(),
    )
    return { entryId, appended: true }
  }

  /** The `mod.schedule_due` entry for an occurrence key, or null. */
  scheduleDueEntry(key: string): EntryId | null {
    for (const e of this.entries()) {
      const p = e.payload
      if (
        p.type === 'extension' &&
        p.payload.extensionType === SCHEDULE_DUE_EXTENSION &&
        String((p.payload.payload as { key?: unknown }).key) === key
      ) {
        return e.id
      }
    }
    return null
  }

  /**
   * RFC 0286 §5a — the single emit path for "a session you dispatched work to
   * has finished that turn", written on the SENDER's log.
   *
   * One line, not the answer. The whole reply is a `read_session` away and that
   * read is bounded; copying it here would put a second transcript on the
   * sender's log per dispatch.
   */
  appendSessionReplied(payload: {
    sessionId: string
    runId: string
    dispatchId: string
    summary: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      SESSION_REPLIED_EXTENSION,
      1,
      {
        sessionId: payload.sessionId,
        runId: payload.runId,
        dispatchId: payload.dispatchId,
        summary: payload.summary,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /**
   * RFC 0289 — the single emit path for "someone answered a comment you
   * wrote", written on the log of the session that wrote it.
   *
   * One line and a pointer, not the reply. The thread is read with the `comment`
   * tool; copying every reply here would grow this log by the length of a
   * conversation that is happening on a document.
   */
  appendCommentReplied(payload: {
    commentId: string
    parentId: string
    documentId?: string
    author: string
    anchorText?: string
    nodeId?: string
    summary: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      COMMENT_REPLIED_EXTENSION,
      1,
      {
        commentId: payload.commentId,
        parentId: payload.parentId,
        // Flat scalars only: an absent field is an empty string on-doc, as with
        // `reason` on an interaction above.
        documentId: payload.documentId ?? '',
        author: payload.author,
        anchorText: payload.anchorText ?? '',
        // Additive within version 1: a reader that predates this field sees the
        // same empty string an unanchored comment has always written, so the
        // version does not move.
        nodeId: payload.nodeId ?? '',
        summary: payload.summary,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /**
   * RFC 0292 — the single emit path for "somebody named you", written on the
   * log of the session that was named.
   *
   * The same shape as `appendCommentReplied` because it is the same act with a
   * different reason to perform it: a pointer and one line, never the prose.
   */
  appendMentioned(payload: {
    commentId: string
    documentId?: string
    author: string
    anchorText?: string
    nodeId?: string
    summary: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      MENTIONED_EXTENSION,
      1,
      {
        commentId: payload.commentId,
        // Flat scalars only: an absent field is an empty string on-doc.
        documentId: payload.documentId ?? '',
        author: payload.author,
        anchorText: payload.anchorText ?? '',
        nodeId: payload.nodeId ?? '',
        summary: payload.summary,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /**
   * RFC 0297 §4a — this session touched a document.
   *
   * `sawUpdatedAt` is the caller's job because only the caller has the document
   * open. Passing 0 says "saw nothing", which reads as stale against any real
   * `updatedAt` and is the safe way to be wrong: it asks the agent to re-read
   * rather than telling it the copy in hand is current.
   */
  appendTouched(payload: {
    documentId: string
    kind: TouchKind
    sawUpdatedAt?: TimestampMs
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      TOUCHED_EXTENSION,
      1,
      {
        documentId: payload.documentId,
        kind: payload.kind,
        sawUpdatedAt: payload.sawUpdatedAt ?? 0,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /** RFC 0297 §4f — this session put a document down. Not a deletion: the next
   *  touch on it brings it back. */
  appendPutDown(payload: { documentId: string; at?: TimestampMs }): EntryId {
    return this.appendExtension(
      PUT_DOWN_EXTENSION,
      1,
      { documentId: payload.documentId, at: payload.at ?? Date.now() },
      this.heads(),
    )
  }

  /** RFC 0301 §3f — a pairing let a signal through. Appended beside the
   *  signal's own entry, before its run is queued. */
  appendFired(payload: {
    pairingId: string
    documentId: string
    signal: FiringSignal
    entryId: EntryId
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      FIRED_EXTENSION,
      1,
      {
        pairingId: payload.pairingId,
        documentId: payload.documentId,
        signal: payload.signal,
        entryId: payload.entryId,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /** RFC 0301 §3d — this session claimed a comment that named nobody. */
  appendCommentClaimed(payload: {
    commentId: string
    documentId: string
    author: string
    anchorText?: string
    nodeId?: string
    summary: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      COMMENT_CLAIMED_EXTENSION,
      1,
      {
        commentId: payload.commentId,
        documentId: payload.documentId,
        author: payload.author,
        anchorText: payload.anchorText ?? '',
        nodeId: payload.nodeId ?? '',
        summary: payload.summary,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /** RFC 0301 S6 — a person edited a paired document and paused. */
  appendEdited(payload: {
    documentId: string
    diff: string
    selectionLabel?: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      EDITED_EXTENSION,
      1,
      {
        documentId: payload.documentId,
        diff: payload.diff,
        selectionLabel: payload.selectionLabel ?? '',
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /** RFC 0313 3j — this session now works on `branch` in `projectId`; '' is main. */
  appendBranch(payload: {
    projectId: string
    branch: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      BRANCH_EXTENSION,
      1,
      {
        projectId: payload.projectId,
        branch: payload.branch,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /** RFC 0303 3e — the person took over a web page this session was driving. */
  appendPageTakenOver(payload: { url: string; at?: TimestampMs }): EntryId {
    return this.appendExtension(
      PAGE_TAKEN_OVER_EXTENSION,
      1,
      { url: payload.url, at: payload.at ?? Date.now() },
      this.heads(),
    )
  }

  /**
   * RFC 0313 3a — the turn a write made now belongs to: the prompt of the run
   * in progress, the newest if several. Null when no run is in progress, as
   * for a write a host makes on its own.
   */
  currentTurn(): EntryId | null {
    let best: { at: number; id: EntryId } | null = null
    for (const e of this.entries()) {
      if (e.payload.type !== 'run') continue
      const status = String(e.payload.payload.status)
      if (status !== 'running' && status !== 'waiting_for_tools') continue
      const at = Number(e.createdAt ?? 0)
      if (!best || at >= best.at) {
        best = { at, id: e.payload.payload.promptMessageId }
      }
    }
    return best?.id ?? null
  }

  /** RFC 0313 3j — the branch this session works on in `projectId`, or '' for main. */
  branchFor(projectId: string): string {
    let best = { at: -1, branch: '' }
    for (const e of this.entries()) {
      if (e.payload.type !== 'extension') continue
      if (e.payload.payload.extensionType !== BRANCH_EXTENSION) continue
      const p = e.payload.payload.payload as Record<string, unknown>
      if (String(p.projectId ?? '') !== projectId) continue
      const at = Number(p.at ?? 0)
      if (at >= best.at) best = { at, branch: String(p.branch ?? '') }
    }
    return best.branch
  }

  /**
   * RFC 0313 3f — whether this log was already told about one undo or restore,
   * so a retry repairs a missing notice without writing a second one.
   */
  hasUndone(
    documentId: string,
    reverts?: string,
    restores?: string[],
  ): boolean {
    const key = (restores ?? []).join(',')
    for (const e of this.entries()) {
      if (e.payload.type !== 'extension') continue
      if (e.payload.payload.extensionType !== UNDONE_EXTENSION) continue
      const p = e.payload.payload.payload as Record<string, unknown>
      if (String(p.documentId ?? '') !== documentId) continue
      if (String(p.reverts ?? '') !== (reverts ?? '')) continue
      if (String(p.restores ?? '') !== key) continue
      return true
    }
    return false
  }

  /** RFC 0313 3f — somebody undid or restored over this session's change. */
  appendUndone(payload: {
    documentId: string
    reverts?: string
    restores?: string[]
    by: string
    byName?: string
    summary: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      UNDONE_EXTENSION,
      1,
      {
        documentId: payload.documentId,
        reverts: payload.reverts ?? '',
        restores: (payload.restores ?? []).join(','),
        by: payload.by,
        byName: payload.byName ?? '',
        summary: payload.summary,
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  /** RFC 0303 3e — the person handed a web page back to this session. */
  appendPageHandedBack(payload: { url: string; at?: TimestampMs }): EntryId {
    return this.appendExtension(
      PAGE_HANDED_BACK_EXTENSION,
      1,
      { url: payload.url, at: payload.at ?? Date.now() },
      this.heads(),
    )
  }

  /** RFC 0301 §3b — the turn `runId` chose to do nothing. */
  /** RFC 0348 3e: what the World agent decided about one stop. */
  appendStopDecided(payload: {
    sessionId: string
    /** The run the decision is about, from the report (RFC 0348 3a). */
    runId?: string
    verdict: StopVerdict
    note?: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      STOP_DECIDED_EXTENSION,
      1,
      {
        sessionId: payload.sessionId,
        ...(payload.runId ? { runId: payload.runId } : {}),
        verdict: payload.verdict,
        note: payload.note ?? '',
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  appendPassed(payload: {
    runId: EntryId
    note?: string
    at?: TimestampMs
  }): EntryId {
    return this.appendExtension(
      PASSED_EXTENSION,
      1,
      {
        runId: payload.runId,
        note: payload.note ?? '',
        at: payload.at ?? Date.now(),
      },
      this.heads(),
    )
  }

  mutate(id: EntryId, fn: (e: Entry) => void): void {
    this.handle.change((d: Draft) => {
      const raw = d[THREAD_KEY]?.entries?.[id]
      if (!raw) return
      const decoded = decodeEntry(raw)
      fn(decoded)
      reencodeScalars(
        raw,
        decoded.payload.payload as unknown as Record<string, unknown>,
      )
      if (decoded.payload.type === 'message') {
        const message = decoded.payload.payload
        if (message.role === 'user') writeStripActivity(d, null)
        else if (message.role === 'assistant') writeStripReplySource(d, id)
      }
    })
  }

  /**
   * Append streamed text to a message's inline `text`.
   *
   * RFC 0317: `text` is an ImmutableString scalar, so a chunk is one op that
   * replaces the field with the longer string. A message written before 0317
   * holds a collaborative Text there, which reads back as a plain string from
   * the draft; that one is still spliced, because assigning over a Text would
   * orphan its ops and a scalar cannot be spliced.
   */
  appendText(id: EntryId, chunk: string): void {
    this.handle.change((d: Draft) => {
      const raw = d[THREAD_KEY]?.entries?.[id]
      // `type` is an ImmutableString on-doc, so coerce before comparing.
      // RFC 0258: on-doc envelope key is `body`.
      if (!raw || String(raw.body.type) !== 'message') return
      const current = raw.body.payload.text
      if (typeof current === 'string') {
        Automerge.splice(
          d,
          [THREAD_KEY, 'entries', id, 'body', 'payload', 'text'],
          current.length,
          0,
          chunk,
        )
        return
      }
      const prior = current == null ? '' : String(current)
      raw.body.payload.text = new Automerge.ImmutableString(prior + chunk)
    })
  }

  /** Append a completed tool_use block to a message's `toolUses` list (live streaming). */
  appendToolUse(id: EntryId, block: ToolUseBlock): void {
    this.handle.change((d: Draft) => {
      const raw = d[THREAD_KEY]?.entries?.[id]
      if (!raw || String(raw.body.type) !== 'message') return
      const data = raw.body.payload as { toolUses?: unknown[] }
      // Automerge list proxies don't always satisfy Array.isArray, so push
      // directly (initializing the list if a message somehow lacks one).
      if (!data.toolUses) data.toolUses = []
      data.toolUses.push(encodeToolUse(block))
    })
  }

  async reference(content: Uint8Array | string): Promise<DocId> {
    if (!this.content) {
      throw new Error('BranchLog.reference: no ContentStore configured')
    }
    return this.content.create(content)
  }

  // ---- tool-worker registry (RFC 0216; shape reserved by 0215) ----

  /** Current registry of identified tool workers (id -> ToolWorker). */
  toolWorkers(): Record<ToolWorkerId, ToolWorker> {
    return this.doc().toolWorkers
  }

  /** Register an identified tool worker as `available`. Idempotent on name. */
  registerToolWorker(
    id: ToolWorkerId,
    name: string,
    opts?: { defaultModel?: string; lastSeen?: number },
  ): void {
    this.handle.change((d: Draft) => {
      if (!d[THREAD_KEY]) initThread(d)
      writeToolWorker(d[THREAD_KEY], id, {
        name,
        status: 'available',
        defaultModel: opts?.defaultModel ?? null,
        lastSeen: opts?.lastSeen ?? null,
      })
    })
  }

  /**
   * Renew a device's liveness lease (RFC 0240): stamp `lastSeen` and mark it
   * `available`, registering it if new. The reaper (reapStaleWorkers) flips a
   * device whose lease lapses to `unreachable`.
   */
  heartbeatToolWorker(
    id: ToolWorkerId,
    name: string,
    at: number,
    opts?: { defaultModel?: string },
  ): void {
    this.handle.change((d: Draft) => {
      if (!d[THREAD_KEY]) initThread(d)
      const existing = d[THREAD_KEY].toolWorkers?.[id] as
        | Record<string, unknown>
        | undefined
      writeToolWorker(d[THREAD_KEY], id, {
        name,
        status: 'available',
        lastSeen: at,
        defaultModel:
          opts?.defaultModel ??
          (existing?.defaultModel != null
            ? String(existing.defaultModel)
            : null),
      })
    })
  }

  /** Set a worker's durable status; `unreachable` is liveness (RFC 0220), never a drain. */
  setToolWorkerStatus(
    id: ToolWorkerId,
    status: ToolWorker['status'],
    currentToolUseId?: string | null,
  ): void {
    this.handle.change((d: Draft) => {
      const tws = d[THREAD_KEY]?.toolWorkers
      const existing = tws?.[id] as Record<string, unknown> | undefined
      if (!existing) return
      const name = String(existing.name)
      // Preserve the RFC 0240 lease/default fields across a status change.
      writeToolWorker(d[THREAD_KEY], id, {
        name,
        status,
        currentToolUseId,
        lastSeen: existing.lastSeen != null ? Number(existing.lastSeen) : null,
        defaultModel:
          existing.defaultModel != null ? String(existing.defaultModel) : null,
      })
    })
  }

  /** Record that subsequent work in this branch moves to a different tool worker. */
  /**
   * Select a tool worker (RFC 0261 slice 3 placement / RFC 0240 handoff). Appends
   * a whiffletree `worker_migration` whose `workerId` becomes the thread's
   * `selected_tool_worker` — the device that may claim this session's runs. This
   * is the whiffletree-native placement mechanism; there is no per-run device
   * field (that was mod's retired `initialToolWorkerId`).
   */
  migrateWorker(
    workerId: ToolWorkerId,
    opts?: { fromWorkerId?: ToolWorkerId; runId?: EntryId; reason?: string },
  ): EntryId {
    return this.append(
      {
        type: 'worker_migration',
        payload: {
          workerId,
          ...(opts?.fromWorkerId ? { fromWorkerId: opts.fromWorkerId } : {}),
          ...(opts?.runId ? { runId: opts.runId } : {}),
          ...(opts?.reason ? { reason: opts.reason } : {}),
        },
      },
      this.heads(),
    )
  }

  // ---- subscribe (workers + UI) ----

  /** Low-level: fire `cb` on every document change (the worker-runtime wake signal). */
  onChange(cb: () => void): Unsubscribe {
    this.handle.on('change', cb)
    return () => this.handle.off('change', cb)
  }

  /** Fire `on` for current and future entries matching `match`, on first appearance and status change. */
  subscribe(match: EntryPredicate, on: (e: Entry) => void): Unsubscribe {
    const seen = new Map<EntryId, string>()
    const fire = () => {
      for (const e of Object.values(this.doc().entries)) {
        if (!match(e)) continue
        const data = e.payload.payload as { status?: string }
        const statusKey = data.status ?? ''
        if (seen.get(e.id) !== statusKey) {
          seen.set(e.id, statusKey)
          on(e)
        }
      }
    }
    const handler = () => fire()
    this.handle.on('change', handler)
    fire()
    return () => this.handle.off('change', handler)
  }

  // ---- project ----

  projectTranscript(): TranscriptMessage[] {
    return projectTranscript(this.doc())
  }

  /** Provider transcript for one recorded causal frontier (RFC 0302 S2). */
  projectTranscriptFromHeads(heads: readonly EntryId[]): TranscriptMessage[] {
    return projectTranscriptFromHeads(this.doc(), heads)
  }

  /** Provider transcript with this run's prompt placed after what it waited for. */
  projectTranscriptForRun(
    heads: readonly EntryId[],
    promptMessageId: EntryId,
  ): TranscriptMessage[] {
    return projectTranscriptForRun(this.doc(), heads, promptMessageId)
  }

  /** Current causal frontier of work descended from one run. */
  lineageHeads(root: EntryId): EntryId[] {
    return lineageHeads(this.doc(), root)
  }

  /**
   * Heads a CONTINUE reads (RFC 0302 S3): the frontier as it stands, minus any
   * other branch's work.
   *
   * The runs to keep out come off the log rather than from the caller, because
   * whether a run is a branch, and whether it has ended, are both recorded on
   * the run itself; a caller collecting them would be re-deriving the routing
   * decision from outside.
   */
  continuationHeads(runId: EntryId): EntryId[] {
    const excluded: EntryId[] = []
    for (const entry of this.entries()) {
      if (entry.payload.type !== 'run') continue
      const run = entry.payload.payload as { branch?: unknown; status: string }
      if (run.branch === true || !RUN_ENDED.has(String(run.status))) {
        excluded.push(entry.id)
      }
    }
    return continuationHeads(this.doc(), runId, excluded)
  }

  projectObjects(): Map<string, ObjectRef> {
    return projectObjects(this.doc())
  }

  projectObjectsWithTombstones(): {
    view: Map<string, ObjectRef>
    deleted: Set<string>
  } {
    return projectObjectsWithTombstones(this.doc())
  }

  /** RFC 0235 — the session's VCS-change history from `mod.session_change` events. */
  projectSessionChanges(): SessionChangeRef[] {
    return projectSessionChanges(this.doc())
  }

  /** RFC 0235 — the session's terminal VCS lifecycle (active / discarded / merged). */
  projectSessionLifecycle(): SessionLifecycle {
    return projectSessionLifecycle(this.doc())
  }

  /** Every interaction on this log, joined to its answer. */
  /**
   * The interactions projection, once per document version.
   *
   * REMEMBERED HERE, WHERE THE VERSION IS KNOWN. A profile of a daemon pinned
   * at a core put the topological sort at 27% of self time: `pendingStops`
   * asked for the pending interactions once per completed run, and each ask
   * sorted the whole log again. A session with thousands of runs did thousands
   * of sorts of ten thousand entries per tick, for an answer that never
   * changed between them. `doc()` already knows when the log moved on, so the
   * projection lives beside `entryList` and goes when it goes.
   */
  projectInteractions(): Interaction[] {
    const doc = this.doc()
    if (!this.interactions) this.interactions = projectInteractions(doc)
    return this.interactions
  }

  /** The interactions still blocking on a human — what a surface renders. */
  pendingInteractions(): Interaction[] {
    return this.projectInteractions().filter((i) => i.status === 'pending')
  }

  /** One interaction by correlation id; what a blocked waiter polls. */
  interactionById(interactionId: string): Interaction | null {
    return (
      this.projectInteractions().find(
        (i) => i.interactionId === interactionId,
      ) ?? null
    )
  }

  /** RFC 0233 — the per-session participant set derived from this session's log. */
  sessionParticipants(): Set<string> {
    return sessionParticipants(this.doc())
  }

  /**
   * RFC 0233 — the per-session sync gate: may `peerMemberId` sync this session?
   * The creator is supplied out-of-band (e.g. the session doc's `createdBy`).
   * This is the predicate a sync server evaluates per (session, peer).
   */
  peerMaySyncSession(
    peerMemberId: string,
    opts?: SessionAccessOptions,
  ): boolean {
    return peerMaySyncSession(this.doc(), peerMemberId, opts)
  }

  // ---- coordination ----

  activeToolWorker(at: EntryId): ToolWorkerId | null {
    return activeToolWorker(this.doc(), at)
  }

  inferenceForkWinner(messageIds: EntryId[]): EntryId | null {
    return inferenceForkWinner(this.doc(), messageIds)
  }
}
